import { useCalculatorStore } from '@/store/calculatorStore'
import Grade from './Grade'
import { Course as CourseType } from '@/data/models'
import { useCalculateCourseImprovement } from '@/hooks/useCalculations'
import { cn } from '@/lib/utils'

const Improvement = (course: CourseType) => {
  const improvement = Number(useCalculateCourseImprovement(course.id).toFixed(1))

  if (improvement === 0) return null

  const sign = improvement >= 0 ? '+' : '-'
  return (
    <div className="flex items-center gap-1" data-testid={`improvement-indicator-${course.id}`}>
      <span
        className={cn(improvement >= 0 ? 'text-green-600' : 'text-red-600')}
        data-testid={`improvement-value-${course.id}`}
      >
        {sign}
        {Math.abs(improvement)} poeng
      </span>
    </div>
  )
}

function CourseImprovement( course: CourseType) {
  const changeGrade = useCalculatorStore((state) => state.changeGrade)

  return (
    <div className="@container" data-testid={`course-improvement-${course.id}`}>
      <div className="@max-[700px]:pb-5 @max-[700px]:pt-6 py-3 @max-[700px]:px-5 border-b border-warm-gray-400 @min-[700px]:border-warm-gray-300 @max-[700px]:shadow-[0_4px_0_0_#F0EDEA] relative grid grid-cols-1 gap-2">
        <div className="@min-[700px]:grid @min-[700px]:grid-cols-[1fr_212px_136px] @min-[700px]:items-center @min-[700px]:gap-6 text-sm">
          <h3 className="@max-[700px]:col-span-3 @max-[700px]:text-base @max-[700px]:mb-5" data-testid={`course-name-${course.id}`}>
            {course.name}
          </h3>
          <div className="grid grid-cols-6 @min-[700px]:grid-cols-6 gap-1 @max-[700px]:h-10">
            <div className="absolute top-5 right-5 flex flex-row gap-1 justify-end @min-[700px]:top-3 @min-[700px]:right-[400px] @min-[700px]:justify-center @min-[700px]:w-[104px]" data-testid={`existing-grades-${course.id}`}>
              <div className="size-7 @min-[700px]:size-8">
                <Grade number={course.grade} isSelected={true} onClick={() => {}} data-testid={`existing-grade-${course.grade}-${course.id}`} />
              </div>
              {course.exam > 1 && (
                <div className="size-7 @min-[700px]:size-8">
                  <Grade
                    number={course.exam}
                    isSelected={true}
                    onClick={() => {}}
                    data-testid={`existing-exam-${course.exam}-${course.id}`}
                  />
                </div>
              )}
              {course.exam2 > 1 && (
                <div className="size-7 @min-[700px]:size-8">
                  <Grade
                    number={course.exam2}
                    isSelected={true}
                    onClick={() => {}}
                    data-testid={`existing-exam2-${course.exam2}-${course.id}`}
                  />
                </div>
              )}
            </div>
            {[1, 2, 3, 4, 5, 6].map((number) => (
              <Grade
                key={number}
                number={number}
                isSelected={course.improved === number}
                onClick={() =>
                  changeGrade(
                    course.id,
                    course.improved === number ? 0 : number,
                    'improved'
                  )
                }
                variant="improvement"
                data-testid={`improve-grade-${number}-${course.id}`}
              />
            ))}
          </div>
          <div className="hidden @min-[700px]:block">
            <Improvement {...course} />
          </div>
        </div>
        {course.hasTwoExams && (
          <div className="@min-[700px]:grid @min-[700px]:grid-cols-[1fr_212px_136px] @min-[700px]:items-center @min-[700px]:gap-6 text-sm">
            <h3 className="hidden @min-[700px]:block text-nowrap ml-4 text-warm-gray-700"></h3>
            <div className="grid grid-cols-6 @min-[700px]:grid-cols-6 gap-1 @max-[700px]:h-10">
              {[1, 2, 3, 4, 5, 6].map((number) => (
                <Grade
                  key={number}
                  number={number}
                  isSelected={course.improved2 === number}
                  onClick={() =>
                    changeGrade(
                      course.id,
                      course.improved2 === number ? 0 : number,
                      'improved2'
                    )
                  }
                  variant="improvement"
                  data-testid={`improve-grade2-${number}-${course.id}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CourseImprovement
